import { reactive } from 'vue';
import makeStoreDebugAccessible from '@/core/store/store-debug';
import { localStorageService } from '@/core/browser/storage.service';
import utilities from '@/shared/utilities.service';
import { B2BClientService, RefreshTokenResponse } from '@/b2b/shared/api/server-apis';
import Cookies from 'js-cookie';
import Deferred from '@/core/async/Deferred';
import { TempAccountApiClientService } from '@/b2b/shared/api/temp-account-api';
import { hardRedirect } from '@/core/navigation/redirect.service';
import { B2CClientService } from '@/b2c/shared/api/server-apis';
import siteContextService from '@/b2b/shared/site-context.service';
import storeContextService from '@/storecontext/shared/site-context.service';
import httpService from '@/core/http/http.service';

export const ACCESS_TOKEN_COOKIE_NAME = 'access_token';
export const REFRESH_TOKEN_COOKIE_NAME = 'refresh_token';
export const SHOW_NET_PRICE_STORAGE_NAME = 'partnerContentState';
export const CUSTOMER_SERVICE_IMPERSONATE_URL = '/customerservice/impersonate/';

export const B2C_USER_ROLE_NAME = 'B2C';
export const B2B_USER_ROLE_NAME = 'B2B';
export const STORE_CONTEXT_USER_ROLE_NAME = 'StoreContext';

class AuthStore {
    private state: IAuthState = reactive({
        accessToken: Cookies.get(ACCESS_TOKEN_COOKIE_NAME) ?? null,
        refreshToken: Cookies.get(REFRESH_TOKEN_COOKIE_NAME) ?? null,
        userData: null,
        showNetPrice: localStorageService.getItemAs<IShowNetPriceStorageState>(SHOW_NET_PRICE_STORAGE_NAME)?.active ?? true,
    });

    constructor() {
        makeStoreDebugAccessible('authStore', this.state);
        this.updateUserData();
    }

    get accessToken(): string | null {
        return this.state.accessToken;
    }

    get refreshToken(): string | null {
        return this.state.refreshToken;
    }

    get userData(): any {
        return this.state.userData || {} as any;
    }

    get isSignedIn(): boolean {
        return this.userData?.userId ?? false;
    }

    get isSignedInAsPartner(): boolean {
        return this.nameIsInUserRoles(B2B_USER_ROLE_NAME);
    }

    get isSignedInAsStore(): boolean {
        return this.nameIsInUserRoles(STORE_CONTEXT_USER_ROLE_NAME);
    }

    get isSignedInAsB2C(): boolean {
        return this.nameIsInUserRoles(B2C_USER_ROLE_NAME);
    }

    get isCustomerService(): boolean {
        return this.userData?.customerServiceImpersonateUserName ?? false;
    }

    get showNetPrice(): boolean {
        return this.state.showNetPrice;
    }

    get hideNetPrice(): boolean {
        return this.nameIsInUserRoles('B2BThirdParty');
    }

    get storeId(): string | null {
        return this.userData?.storeId ?? null;
    }

    nameIsInUserRoles(name: string): boolean {
        if (!this.userData.role) {
            return false;
        }
        const roles = Array.isArray(this.userData.role) ? this.userData.role : [this.userData.role];
        return roles.some(r => r === name);
    }

    toggleNetPrice(): void {
        this.state.showNetPrice = !this.state.showNetPrice;
        const state: IShowNetPriceStorageState = {
            active: this.state.showNetPrice,
        };
        localStorageService.setItemAs<IShowNetPriceStorageState>(SHOW_NET_PRICE_STORAGE_NAME, state);
    }

    clearAuthentication(): void {
        this.updateAccessToken(null);
        this.updateRefreshToken(null);
    }

    resetAuthentication(accessToken: string, refreshToken?: string): void {
        this.updateAccessToken(accessToken);
        refreshToken && this.updateRefreshToken(refreshToken);
    }

    async userLogout(): Promise<void> {
        if (this.isSignedInAsStore) {
            hardRedirect(storeContextService.topNavigation.logoutUrl);
        } else if (this.isSignedInAsPartner) {
            await httpService.post(siteContextService.logoutEndpointUrl);
        } else {
            await B2CClientService.logout();
            this.clearAuthentication();
            hardRedirect(siteContextService.frontpageUrl);
        }
    }

    async customerServiceSignOut(): Promise<any> {
        const signOut = await TempAccountApiClientService.signOut();
        hardRedirect(CUSTOMER_SERVICE_IMPERSONATE_URL);
        return signOut;
    }

    async tryRefreshExpiredAccessToken(): Promise<RefreshTokenResponse | undefined> {
        const deferred = new Deferred<RefreshTokenResponse | undefined>();
        if (!this.refreshToken) {
            deferred.reject();
        }
        const api = this.isSignedInAsPartner ? B2BClientService : B2CClientService;
        const refresh = await api.refreshToken(this.refreshToken).then(utilities.unwrapModel);
        if (refresh?.success) {
            this.updateAccessToken(refresh.token ?? null);
            this.updateRefreshToken(refresh.refreshToken ?? null);
            deferred.resolve(refresh);
        } else {
            deferred.reject();
        }
        return deferred.promise;
    }

    private updateAccessToken(token: string | null): void {
        this.state.accessToken = token;
        token ? Cookies.set(ACCESS_TOKEN_COOKIE_NAME, token) : Cookies.remove(ACCESS_TOKEN_COOKIE_NAME);
        this.updateUserData();
    }

    private updateRefreshToken(token: string | null): void {
        this.state.refreshToken = token;
        token ? Cookies.set(REFRESH_TOKEN_COOKIE_NAME, token) : Cookies.remove(REFRESH_TOKEN_COOKIE_NAME);
    }

    private updateUserData(): void {
        this.state.userData = this.parseJwt(this.state.accessToken);
    }

    private parseJwt(token: string | null): any | null {
        if (!token) return null;

        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            // eslint-disable-next-line
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));

            const parsedToken = JSON.parse(jsonPayload);
            const tokenData = {};
            Object.keys(parsedToken).forEach(key => {
                const pascalCasedKey = key.charAt(0).toLowerCase() + key.slice(1);
                tokenData[pascalCasedKey] = parsedToken[key];
            });
            return tokenData;
        } catch {
            this.updateAccessToken(null);
            return null;
        }
    }
}

export type IShowNetPriceStorageState = {
    active: boolean;
}

export interface IAuthState {
    accessToken: string | null;
    refreshToken: string | null;
    userData: string | null;
    showNetPrice: boolean;
}

export default new AuthStore();
